export interface MentorLesson {
  id: string;
  order: number;
  title: string;
  durationText: string;
  videoUrl: string; // YouTube link editável ou iframe URL
  thumbnailUrl: string;
  publishedAtText: string;
  description?: string;
  isCompleted?: boolean;
}

export interface MentorModule {
  id: string;
  order: number;
  title: string;
  description?: string;
  coverImageUrl: string; // Imagem vertical do pôster do módulo (proporção 3:4)
  accentGlowColor?: string; // Hex ou classe CSS de brilho
  lessons: MentorLesson[];
}

export interface MentorEvent {
  id: string;
  title: string;
  dateText: string; // ex: "15 de Agosto às 20h"
  timeText?: string;
  description: string;
  meetingUrl?: string; // Link do Zoom/Meet
  isLive?: boolean;
}

export interface MentorSupportConfig {
  photoUrl?: string;
  whatsappNumber?: string; // ex: "5511999999999"
  message?: string;
  workingHours?: string;
}

export interface MentorStudent {
  id: string;
  name: string;
  email: string;
  accessDateText: string;
  progressPercentage: number;
  durationDays?: number;
  expiresAt?: string;
  status?: 'active' | 'expired' | 'pending';
}

export interface BioLinkItem {
  id: string;
  title: string;
  subtitle?: string;
  url: string;
  icon?: string; // Nome do ícone ("Zap", "WhatsApp", "Instagram", "Globe", "Video", "TikTok", "LinkedIn", "Twitter", "GitHub", "Book", "Award", "Custom") ou URL de imagem
  thumbnailUrl?: string; // URL da imagem ou ícone personalizado (ex: PNG/WebP 64x64 ou 128x128)
  iconUrl?: string; // Sinônimo para thumbnailUrl
  isFeatured?: boolean; // Card destacado com brilho verde neon e animação de pulso
  badgeText?: string; // ex: "🔥 MAIS POPULAR", "⚡ MENTORIA VIP"
}

// Entrada individual de link de rede social (suporta múltiplos por rede)
export interface SocialEntry {
  url: string;
  label?: string; // ex: "Santiago Suso", "Thomas Suso"
}

// Cada campo aceita um array de entradas (multi-link)
export interface MentorSocialLinks {
  instagram?: SocialEntry[];
  youtube?: SocialEntry[];
  whatsapp?: SocialEntry[];
  linkedin?: SocialEntry[];
  website?: SocialEntry[];
  tiktok?: SocialEntry[];
  twitter?: SocialEntry[];
  github?: SocialEntry[];
}

/**
 * Normaliza o campo socials para sempre usar SocialEntry[].
 * Garante retrocompatibilidade com dados antigos onde os campos eram strings simples.
 */
export function normalizeSocials(raw: Record<string, any> | undefined): MentorSocialLinks {
  if (!raw) return {};
  const keys: (keyof MentorSocialLinks)[] = [
    'instagram', 'youtube', 'whatsapp', 'linkedin',
    'website', 'tiktok', 'twitter', 'github',
  ];
  const result: MentorSocialLinks = {};
  for (const key of keys) {
    const val = raw[key];
    if (!val) continue;
    if (typeof val === 'string') {
      if (val.trim()) result[key] = [{ url: val.trim() }];
    } else if (Array.isArray(val)) {
      const entries: SocialEntry[] = val
        .map((v: any) => {
          if (typeof v === 'string') return v.trim() ? { url: v.trim() } : null;
          if (v && typeof v === 'object' && v !== null) {
            const rawUrl = typeof v.url === 'string' ? v.url : '';
            const rawLabel = typeof v.label === 'string' ? v.label : undefined;
            if (rawUrl || rawLabel) {
              return { url: rawUrl, label: rawLabel };
            }
          }
          return null;
        })
        .filter(Boolean) as SocialEntry[];
      if (entries.length) result[key] = entries;
    }
  }
  return result;
}

export interface MentorBioConfig {
  isEnabled: boolean;
  slug: string; // ex: "vitor-terra" -> /bio-vitor-terra
  bioTitle: string;
  bioSubtitle?: string;
  bioAvatarUrl: string;
  socials: MentorSocialLinks;
  links: BioLinkItem[];
  theme?: {
    accentColor?: string; // default: "#0FFF55"
    backgroundColor?: string; // default: "#0d0e11"
  };
}

export interface MentorAreaConfig {
  id: string;
  slug: string; // ex: 'vitor-terra' -> /mentoria-vitor-terra
  mentorName: string;
  mentorTitle: string; // ex: 'Especialista em Engenharia de AI & Prompting'
  mentorAvatarUrl: string;
  courseTitle: string; // ex: 'Ventture Academy' ou 'Masterclub Mentorship'
  tagline?: string; // ex: 'Sua Jornada Começa Aqui'
  heroSpoiler: {
    title: string;
    description: string;
    youtubeUrl: string; // Link editável do YouTube para o spoiler / trailer
    durationText: string; // ex: '3 min'
  };
  branding: {
    primaryColor: string; // ex: '#121316'
    accentColor: string; // ex: '#0FFF55' (Verde Masterclub)
    coverImageUrl: string;
    bannerBadge?: string; // ex: '1.00' ou 'Membro VIP'
  };
  modules: MentorModule[];
  support?: MentorSupportConfig;
  events?: MentorEvent[];
  students?: MentorStudent[];
  bioConfig?: MentorBioConfig;
  accessDurationDays?: number; // Duração limite do acesso em dias (ex: 365 por padrão, editável pelo mentor)
  mentorUserId?: string | null; // ID do usuário proprietário desta mentoria (auth.users)
}

export interface MentorshipProgress {
  mentorshipId: string;
  completedLessonIds: string[];
}
